import { HttpError } from '@littlebird/auth-client';
import { isChatStatusBusy, useStores } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect } from 'react';

import { YStack } from '@/components/ui/layout/layout';
import {
  BROWSER_MIN_WIDTH,
  chatRowLayoutStore,
  CHAT_MIN_WIDTH,
} from '@/features/chat/chat-row-layout.store';
import { useEdgeResize } from '@/hooks/useEdgeResize';
import { cn } from '@/lib/utils';

import { chatHasActiveBrowserUse } from './browser-stream-activity';
import { useBrowserStreamQuery } from './browser-stream.query';
import { ChatCredentialRequest } from './ChatCredentialRequest';
import { ChatSavedCredentialRequest } from './ChatSavedCredentialRequest';
import { LiveBrowserStreamCard } from './LiveBrowserStreamCard';
import {
  isSavedRequestOpen,
  useChatSavedCredentialRequestQuery,
} from './saved-credential.query';
import { useChatSignInSessionQuery } from './sign-in-session.query';
import { useBrowserStreamInput } from './use-browser-stream-input';
import { useBrowserViewer } from './use-browser-viewer';

export const ChatBrowserStreamPanel = observer(
  ({ threadId, isSelected }: { threadId: string; isSelected: boolean }) => {
    const { messages, threads } = useStores();
    const threadMessages = messages.getMessagesByThreadId(threadId);
    const browserActive = chatHasActiveBrowserUse(threadMessages);
    const chatBusy = isChatStatusBusy(threads.getById(threadId)?.status);
    const { data: signInSession } = useChatSignInSessionQuery(
      threadId,
      isSelected,
      chatBusy
    );
    const { data: savedRequest } = useChatSavedCredentialRequestQuery(
      threadId,
      isSelected,
      chatBusy
    );
    const savedRequestVisible = isSavedRequestOpen(savedRequest);
    const requestActive = signInSession?.status === 'active';
    const needsNotification =
      signInSession?.request?.status === 'resolved' &&
      !signInSession.request.notified;
    const isAgentBusy =
      !savedRequestVisible &&
      signInSession?.request?.status !== 'pending' &&
      chatBusy;
    const hasBrowser =
      browserActive ||
      requestActive ||
      needsNotification ||
      savedRequestVisible;
    const { isBrowserFullscreen, isBrowserFloating } = chatRowLayoutStore;
    const canDock = chatRowLayoutStore.browserDock.canDock;
    const dockedWidth = chatRowLayoutStore.browserDock.dockedWidth;
    useEffect(() => {
      if (isSelected && (requestActive || savedRequestVisible))
        chatRowLayoutStore.restoreBrowser();
    }, [isSelected, requestActive, savedRequestVisible]);
    const viewer = useBrowserViewer(threadId, hasBrowser, isSelected);
    const isBrowserVisible = chatRowLayoutStore.isBrowserVisible;
    const { iframeRef, canInteract, takeControl, returnControl } =
      useBrowserStreamInput(isSelected && viewer.isViewing && isBrowserVisible);
    const streamQuery = useBrowserStreamQuery(
      threadId,
      hasBrowser && viewer.isViewing,
      isAgentBusy,
      signInSession
        ? `${signInSession.sign_in_session_id}:${signInSession.status}:${signInSession.profile_released ?? false}`
        : undefined
    );
    const browserMissing =
      signInSession != null &&
      signInSession.status !== 'active' &&
      !needsNotification &&
      !savedRequestVisible &&
      streamQuery.error instanceof HttpError &&
      streamQuery.error.status === 404;
    const panelOpen = hasBrowser && !browserMissing;
    useEffect(() => {
      if (!isSelected) return;
      chatRowLayoutStore.setBrowserOpen(panelOpen);
      return () => chatRowLayoutStore.setBrowserOpen(false);
    }, [panelOpen, isSelected]);
    const { onResizeStart } = useEdgeResize({
      axis: 'x',
      min: BROWSER_MIN_WIDTH,
      max: () =>
        chatRowLayoutStore.rowWidth > 0
          ? chatRowLayoutStore.rowWidth - CHAT_MIN_WIDTH
          : window.innerWidth * 0.8,
      getStartSize: () => chatRowLayoutStore.browserDock.dockedWidth,
      onResize: (next) => chatRowLayoutStore.setBrowserWidth(next),
    });

    const { refetch } = streamQuery;
    const handleRefresh = useCallback(() => {
      if (viewer.canConnect()) void refetch();
    }, [viewer, refetch]);
    const handleTakeControl = useCallback(() => {
      viewer.extend();
      takeControl();
    }, [viewer, takeControl]);

    if (!panelOpen) return null;

    return (
      <YStack
        className={cn(
          'h-full pt-2 pr-2 pb-2',
          isBrowserFloating ? 'absolute inset-0 z-30 pl-2' : 'relative shrink-0'
        )}
        style={{
          width: isBrowserFloating ? undefined : dockedWidth,
          display: isSelected && isBrowserVisible ? undefined : 'none',
        }}
        data-testid="chat-browser-stream-panel"
      >
        {!isBrowserFloating && (
          <div
            onMouseDown={onResizeStart}
            className="absolute top-2 bottom-2 left-0 z-10 w-1.5 cursor-ew-resize"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize browser panel"
          />
        )}
        <YStack className="h-full w-full overflow-hidden rounded-[8px] border border-border bg-surface">
          <YStack className="min-h-0 flex-1">
            <LiveBrowserStreamCard
              isPaused={!viewer.isViewing}
              canResume={viewer.available}
              onResume={viewer.resume}
              iframeRef={iframeRef}
              canInteract={canInteract}
              streamUrl={
                viewer.isViewing ? streamQuery.data?.stream_url : undefined
              }
              isLoading={streamQuery.isLoading || streamQuery.isFetching}
              isFullscreen={isBrowserFullscreen}
              canToggleFullscreen={canDock}
              onToggleFullscreen={() =>
                chatRowLayoutStore.toggleBrowserFullscreen()
              }
              onClose={() => {
                returnControl();
                chatRowLayoutStore.dismissBrowser();
              }}
              onRefresh={handleRefresh}
              onTakeControl={handleTakeControl}
              onReturnControl={returnControl}
            >
              {savedRequestVisible ? (
                <ChatSavedCredentialRequest
                  request={savedRequest}
                  onBeforeSubmit={returnControl}
                />
              ) : (
                <ChatCredentialRequest
                  session={signInSession}
                  onBeforeSubmit={returnControl}
                />
              )}
            </LiveBrowserStreamCard>
          </YStack>
        </YStack>
      </YStack>
    );
  }
);
